import { collegeDirectory } from './collegeData.js';
import { getExamDirectoryEntries } from './examCollegeDirectory.js';
import { ugmacColleges } from './ugmacData.js';

export const staticSearchItems = [
  { label: 'Home', path: '/', section: 'Main', keywords: 'home dashboard main' },
  { label: 'Predictor Hub', path: '/predictor', section: 'Predictor', keywords: 'predictor chance admission eligibility' },
  { label: 'Seat Matrix & Cutoff Hub', path: '/cutoff-trends', section: 'Data', keywords: 'cutoff seat matrix ranks closing rank' },
  { label: 'Cutoff Trends Comparison (2025 vs 2026)', path: '/cutoff-trends-comparison', section: 'Data', keywords: 'trends movement comparison 2025 2026 inflation' },
  { label: 'Colleges Hub', path: '/colleges', section: 'Colleges', keywords: 'colleges directory institute campus' },
  { label: 'Admission Guide Hub', path: '/admission-guide', section: 'Guides', keywords: 'admission guide counselling steps documents dates' },
  { label: 'Resources & BEU Syllabus', path: '/resources', section: 'Resources', keywords: 'resources syllabus beu notes previous papers' },
  { label: 'Engineering Predictor (UGEAC)', path: '/college-predictor', section: 'Engineering', keywords: 'ugeac jee main engineering btech predictor' },
  { label: 'UGEAC Cutoff & Seat Matrix', path: '/ugeac-cutoff-seat-matrix', section: 'Engineering', keywords: 'ugeac jee main cutoffs seats engineering' },
  { label: 'BCECE Engineering Predictor', path: '/bcece-engineering-predictor', section: 'Engineering', keywords: 'bcece pcm engineering predictor' },
  { label: 'BCECE Engineering Cutoff & Seat Matrix', path: '/bcece-engineering-cutoff-seat-matrix', section: 'Engineering', keywords: 'bcece pcm engineering cutoffs seats' },
  { label: 'UGMAC Medical Predictor', path: '/ugmac-predictor', section: 'Medical', keywords: 'ugmac neet mbbs bds medical predictor' },
  { label: 'UGMAC Cutoff & Seat Matrix', path: '/ugmac-cutoff-seat-matrix', section: 'Medical', keywords: 'ugmac neet medical cutoffs mbbs bds' },
  { label: 'UGMAC Ayush Predictor', path: '/ugmac-ayush-predictor', section: 'AYUSH', keywords: 'ugmac ayush bams bhms bums predictor' },
  { label: 'UGMAC Ayush Cutoff & Seat Matrix', path: '/ugmac-ayush-cutoff-seat-matrix', section: 'AYUSH', keywords: 'ugmac ayush bams bhms bums cutoffs seats' },
  { label: 'Polytechnic Predictor (DCECE PE)', path: '/polytechnic-predictor', section: 'Polytechnic', keywords: 'dcece pe diploma polytechnic predictor' },
  { label: 'Polytechnic Cutoff & Seat Matrix', path: '/polytechnic-cutoff-seat-matrix', section: 'Polytechnic', keywords: 'dcece pe polytechnic cutoffs seats' },
  { label: 'Paramedical Predictor (DCECE PM/PMM)', path: '/paramedical-predictor', section: 'Paramedical', keywords: 'dcece pm pmm anm gnm paramedical predictor' },
  { label: 'Paramedical Cutoff & Seat Matrix', path: '/paramedical-cutoff-seat-matrix', section: 'Paramedical', keywords: 'dcece pm pmm anm gnm cutoffs seats' },
];

const buildKeywords = (...values) =>
  values
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

let cachedGlobalSearchItems = null;

export const getGlobalSearchItems = () => {
  if (cachedGlobalSearchItems) {
    return cachedGlobalSearchItems;
  }

  const engineeringCollegeItems = collegeDirectory.map((college) => ({
    label: college.name,
    path: `/colleges/${college.slug}`,
    section: 'Engineering College',
    keywords: buildKeywords(college.shortName, college.city, college.district, college.branches),
  }));

  const ugmacCollegeItems = ugmacColleges.map((college) => ({
    label: college.name,
    path: `/ugmac-colleges/${college.slug}`,
    section: 'Medical College',
    keywords: buildKeywords(college.shortName, college.city, college.district, college.courses),
  }));

  const searchDirectoryConfigs = [
    {
      section: 'BCECE Engineering College',
      basePath: '/bcece-engineering-colleges',
      entries: getExamDirectoryEntries('bceceEngineering'),
    },
    {
      section: 'Paramedical Institute',
      basePath: '/paramedical-colleges',
      entries: getExamDirectoryEntries('paramedical'),
    },
    {
      section: 'Polytechnic Institute',
      basePath: '/polytechnic-colleges',
      entries: getExamDirectoryEntries('polytechnic'),
    },
    {
      section: 'UGMAC Ayush College',
      basePath: '/ugmac-ayush-colleges',
      entries: getExamDirectoryEntries('ugmacAyush'),
    },
  ];

  const examCollegeItems = searchDirectoryConfigs.flatMap(({ section, basePath, entries }) =>
    entries.map((entry) => ({
      label: entry.name,
      path: `${basePath}/${entry.slug}`,
      section,
      keywords: buildKeywords(entry.instituteType, entry.courses, entry.courseFamilies),
    })),
  );

  const merged = [...staticSearchItems, ...engineeringCollegeItems, ...ugmacCollegeItems, ...examCollegeItems];
  const seen = new Set();

  cachedGlobalSearchItems = merged.filter((item) => {
    if (seen.has(item.path)) {
      return false;
    }
    seen.add(item.path);
    return true;
  });

  return cachedGlobalSearchItems;
};

export const getSectionBadgeStyle = (section = '') => {
  const s = section.toLowerCase();
  if (s.includes('college') || s.includes('institute') || s.includes('engineering') || s.includes('medical')) {
    return 'bg-blue-50 text-blue-700 border-blue-100/80 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/50';
  }
  if (s.includes('predictor')) {
    return 'bg-amber-50 text-amber-700 border-amber-100/80 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/50';
  }
  if (s.includes('guide')) {
    return 'bg-emerald-50 text-emerald-700 border-emerald-100/80 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900/50';
  }
  if (s.includes('data') || s.includes('cutoff') || s.includes('matrix')) {
    return 'bg-purple-50 text-purple-700 border-purple-100/80 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-900/50';
  }
  if (s.includes('resource') || s.includes('syllabus')) {
    return 'bg-teal-50 text-teal-700 border-teal-100/80 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-900/50';
  }
  return 'bg-slate-50 text-slate-700 border-slate-150 dark:bg-slate-800/40 dark:text-slate-300 dark:border-slate-700/50';
};
